import {
  Stack,
  Select,
  Group,
  Textarea,
  Alert,
  Text,
  Chip,
  Box,
  ScrollArea,
  TextInput,
  SegmentedControl,
} from '@mantine/core';
import { PhotoInput } from '@/pages/ainigma/patient/examination/photos/PhotoInput';
import { UseFormReturnType } from '@mantine/form';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Specimen,
  Patient,
  CodeableConcept,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { ResourceInput } from '@medplum/react';
import { ARKON } from '@consts/systems.consts';
import { CodeableConceptInput } from '@/components/medplum-react/CodeableConceptInput/CodeableConceptInput';
import { SpecimenFormData } from '../hooks/useSpecimenForm';
import { SPECIMEN_STATUS } from '../constants/specimen.consts';

interface SpecimenCreateEditFormProps {
  form: UseFormReturnType<SpecimenFormData>;
  specimen?: Specimen | null;
  patient: Patient | undefined;
  defaultSampleType?: CodeableConcept;
  selectedPatient: Patient | undefined;
  selectedOrders: ServiceRequest[];
  availableOrders: ServiceRequest[];
  loadingOrders: boolean;
  onPatientChange: (patient: Patient | undefined) => void;
  onOrdersChange: (orderIds: string[]) => void;
  hideOrderSelection?: boolean;
}

export function SpecimenCreateEditForm({
  form,
  specimen,
  patient,
  defaultSampleType,
  selectedPatient,
  selectedOrders,
  availableOrders,
  loadingOrders,
  onPatientChange,
  onOrdersChange,
  hideOrderSelection = false,
}: SpecimenCreateEditFormProps): JSX.Element {
  const { t } = useTranslation('lims');

  return (
    <Stack gap="md">
      {/* Patient Selection - only show when not in patient context */}
      <ResourceInput
        resourceType="Patient"
        name="patient"
        label={t('specimenModal.patient')}
        placeholder={t('specimenModal.patientPlaceholder')}
        required
        key={`patient-${selectedPatient?.id || 'new'}`}
        defaultValue={patient || selectedPatient}
        onChange={onPatientChange}
        disabled={!!patient}
      />

      <CodeableConceptInput
        path="Specimen.type"
        binding={ARKON.VS.SPECIMEN_TYPES_EL}
        label={t('specimenModal.sampleType')}
        name="type"
        maxValues={1}
        required
        defaultValue={specimen?.type || defaultSampleType || undefined}
        onChange={(c) => {
          form.setFieldValue('type', c);
        }}
        placeholder={t('specimenModal.sampleTypePlaceholder')}
        loadAll
        disabled={!!defaultSampleType}
      />

      <TextInput
        label={t('specimenModal.externalId')}
        placeholder={t('specimenModal.externalIdPlaceholder')}
        {...form.getInputProps('externalId')}
      />

      <DateInputWrapper
        label={t('specimenModal.collectionDate')}
        placeholder={t('specimenModal.collectionDatePlaceholder')}
        required
        {...form.getInputProps('collectionDate')}
      />

      <DateInputWrapper
        label={t('specimenModal.registrationDate')}
        placeholder={t('specimenModal.registrationDatePlaceholder')}
        required
        {...form.getInputProps('registrationDate')}
      />

      {/* Quality: Good / Not Good toggle */}
      <Stack gap={2}>
        <Text size="sm" fw={500}>
          {t('specimenModal.quality')}
        </Text>
        <SegmentedControl
          data={[
            { value: 'true', label: t('specimen.conditions.satisfactory') },
            { value: 'false', label: t('specimen.conditions.unsatisfactory') },
          ]}
          value={String(form.values.qualityGood)}
          onChange={(v) => {
            form.setFieldValue('qualityGood', v === 'true');
            if (v === 'true') {
              form.setFieldValue('qualityReason', '');
            }
          }}
        />
        {!form.values.qualityGood && (
          <TextInput
            label={t('specimenModal.qualityReason')}
            placeholder={t('specimenModal.qualityReasonPlaceholder')}
            {...form.getInputProps('qualityReason')}
          />
        )}
      </Stack>

      <Select
        label={t('specimenModal.status')}
        placeholder={t('specimenModal.statusPlaceholder')}
        required
        data={Object.keys(SPECIMEN_STATUS).map((key) => {
          const value = SPECIMEN_STATUS[key as keyof typeof SPECIMEN_STATUS];
          return {
            value,
            label: t(`specimen.statuses.${value}`, value, { ns: 'lims' }),
          };
        })}
        {...form.getInputProps('status')}
      />

      <PhotoInput
        label={t('specimenModal.photos')}
        onChange={(photos) => form.setFieldValue('photos', photos)}
        initialPhotos={form.values.photos}
        maxPhotos={5}
      />

      {/* Order Selection - show for both patient and global contexts with patient filtering */}
      {!hideOrderSelection && (
        <>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t('specimenModal.laboratoryOrders')}
            </Text>
            <Text size="xs" c="dimmed" mt={-10}>
              {t('specimenModal.laboratoryOrdersNote')}
            </Text>

            {loadingOrders ? (
              <Text size="xs">{t('specimenModal.loadingOrders')}</Text>
            ) : availableOrders.length === 0 ? (
              <Text size="xs" c="dimmed">
                {t('specimenModal.noCompatibleOrders')}
              </Text>
            ) : (
              <ScrollArea.Autosize mah={200}>
                <Box>
                  <Chip.Group
                    multiple
                    value={selectedOrders.map((order) => order.id || '')}
                    onChange={onOrdersChange}
                  >
                    <Group gap="xs">
                      {availableOrders.map((order) => (
                        <Chip key={order.id} value={order.id || ''} size="sm">
                          {`${order.identifier?.[0]?.value || order.id} - ${order.code?.coding?.[0]?.display || 'Unknown Test'}`}
                        </Chip>
                      ))}
                    </Group>
                  </Chip.Group>
                </Box>
              </ScrollArea.Autosize>
            )}
          </Stack>

          {/* Show information when orders are selected */}
          {selectedOrders.length > 0 && (
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              <Text size="sm">
                <strong>{t('specimenModal.orderLinksInfo')}:</strong>{' '}
                {t('specimenModal.orderLinksMessage', {
                  count: selectedOrders.length,
                  plural: selectedOrders.length > 1 ? 's' : '',
                })}
              </Text>
              <Stack gap="xs" mt="xs">
                {selectedOrders.map((order) => (
                  <Text key={order.id} size="xs" c="dimmed">
                    • {order.identifier?.[0]?.value || order.id} -{' '}
                    {order.code?.coding?.[0]?.display ||
                      t('specimenDetails.unknownTest')}{' '}
                    ({order.status})
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}
        </>
      )}

      <Textarea
        label={t('specimenModal.notes')}
        placeholder={t('specimenModal.notesPlaceholder')}
        rows={3}
        {...form.getInputProps('notes')}
      />
    </Stack>
  );
}
